import { call, put, select, delay } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { t } from "i18next";

import {
  ISample,
  IApiError,
  ToggleActionType,
  AlertStatusMessage, DialogMode, GetManySamplesStatus, ICart,
} from '@/shared/types';

import { commonSlice, selectActiveParentEntity, selectUserCart } from '@/entities/common';
import { alertsSagaActions } from "@/entities/alerts";

import { dialogSagaActions, dialogSlice } from "@/widgets/dialog";
import { asideSagaActions, asideSlice } from "@/widgets/side-block";

import { selectParams } from "../model/selectors";
import { samplesSlice } from "../model/slice";
import { IGetManySamplesParams } from "../model/types/interfaces";
import {
  getCreateDtoByFormData,
  getUpdateDtoByFormData,
} from "./helpers";
import {
  sendCustomRequest,
  sendGetSampleRequest,
  sendAddItemsByCustomRequest,
  sendCreateSampleRequest,
  sendUpdateSampleRequest,
  sendDeleteSampleRequest,
  sendGetManySamplesRequest, sendImportSamplesRequest, sendSetPriceRequest, sendGetExportSamplesRequest,
  sendGetManySamplesByIdRequest,
} from './senders';
import {
  SetPricePayload,
  GetSamplePayload,
  GetSampleResponse,
  CreateSamplePayload,
  UpdateSamplePayload,
  DeleteSamplePayload,
  CustomRequestPayload,
  ImportSamplesPayload,
  ExportSamplesPayload,
  GetManySamplesPayload,
  GetManySamplesResponse,
  PlaceCustomRequestPayload,
  GetManySamplesByIdPayload,
  GetManySamplesByLoincPayload, AddItemsByRequestPayload,
} from './types/types';
import { sendCreateCartRequest } from '@/entities/common/api/senders';
import { AddItemToCartResponse, CreateCartResponse, RemoveItemFromCartResponse } from '@/entities/common/api/workers';

type IUserCartPayload = {
  cart: ICart | null;
};

export function* handleCreateSample(
  action: PayloadAction<CreateSamplePayload>
): Generator<any, void, any> {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleSideBlock } = asideSagaActions;

  const createDto: Partial<ISample> = yield call(
    getCreateDtoByFormData,
    formData
  );

  try {
    yield call(sendCreateSampleRequest, createDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("sample_created_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleSideBlock({ status: ToggleActionType.CLOSE }));
    yield call(handleRetrieveManySamples);
  }
}

export function* handleUpdateSample(
  action: PayloadAction<UpdateSamplePayload>
): Generator<any, void, any> {
  const { sampleId, formData } = action.payload;

  const { updateAlert } = alertsSagaActions;
  const { toggleSideBlock } = asideSagaActions;

  const updateDto: Partial<ISample> = yield call(
    getUpdateDtoByFormData,
    formData
  );

  try {
    yield call(sendUpdateSampleRequest, sampleId, updateDto);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("sample_updated_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleSideBlock({ status: ToggleActionType.CLOSE }));
    yield call(handleRetrieveManySamples);
  }
}

export function* handleRetrieveSample(action: PayloadAction<GetSamplePayload>): Generator<any, void, any> {
  const { sampleId } = action.payload;
  const { setActiveEntity, setActiveParentEntity } = commonSlice;
  const { setLoadingAside } = asideSlice;
  const { setLoadingDialog } = dialogSlice;

  try {
    const response: GetSampleResponse = yield call(
      sendGetSampleRequest,
      sampleId
    );
    const sample: ISample = response.data.data;

    const modifiedSample = {
      ...sample,
      loincBiomarker: {...sample.loincBiomarker, fullName: `${sample.loincBiomarker.id} ${sample.loincBiomarker.displayName}`},
      donorDob: sample.donorDob,
      collectionDate: sample.collectionDate,
      expirationDate: sample.expirationDate,
      biomarkerValueUom: `${sample?.biomarkerValue} ${sample?.uom}`,
    };

    yield put(setActiveEntity({ activeEntity: modifiedSample }));

    const currentParent = yield select(selectActiveParentEntity);
    if (!currentParent && modifiedSample?.loincBiomarker) {
      yield put(setActiveParentEntity({ parentEntity: modifiedSample.loincBiomarker }));
    }
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

export function* handleCustomRequest(action: PayloadAction<CustomRequestPayload>): Generator<any, void, any> {
  const { setLoadingAside } = asideSlice;
  const { setAvailableSamples } = samplesSlice;

  try {
    yield put(setLoadingAside({ isLoading: true }));
    yield delay(1000);
    const response: GetSampleResponse = yield call(
      sendCustomRequest,
      action.payload
    );
    const samples: any = response.data.data;

    yield put(setAvailableSamples({ data: samples }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingAside({ isLoading: false }));
  }
}

export function* handleRetrieveManySamplesByLOINC(
  action: PayloadAction<IGetManySamplesParams>
): Generator<any, void, any> {
  const { setLoadingDialog } = dialogSlice;
  const { setAvailableSamplesByLoinc } = samplesSlice;

  try {
    yield put(setLoadingDialog({ isLoading: true }));

    const bodyParams: IGetManySamplesParams = action.payload;

    const response: GetManySamplesResponse = yield call(
      sendGetManySamplesRequest,
      bodyParams
    );

    const count: number = response?.data?.data?.count ?? 0;

    yield put(setAvailableSamplesByLoinc({ data: count }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingDialog({ isLoading: false }));
  }
}

function* handlePlaceCustomRequest(action: PayloadAction<AddItemsByRequestPayload>): Generator<any, void, any> {
  const { setUserCart } = commonSlice;
  const { toggleDialog } = dialogSagaActions;
  const { setLoadingDialog } = dialogSlice;
  const { updateAlert } = alertsSagaActions;
  let cart: ICart | null = yield select(selectUserCart);

  if (!cart) {
    const response: CreateCartResponse = yield call(sendCreateCartRequest);
    cart = response.data.data;
  }

  try {
    yield put(setLoadingDialog({ isLoading: true }))
    yield delay(1500);
    const response: AddItemToCartResponse = yield call(
      sendAddItemsByCustomRequest,
      cart!.id as string,
      action.payload
    );

    yield put(setUserCart({ cart: response.data.data }));
    yield put(setLoadingDialog({ isLoading: false }));
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("samples_added_successfully")}`,
      })
    );
  } catch (err) {
    yield put(setLoadingDialog({ isLoading: false }));
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
    yield call(handleError, err);
  }
}

export default handlePlaceCustomRequest;

export function* handleRetrieveManySamples(
  action?: PayloadAction<GetManySamplesPayload>
): Generator<any, void, any> {
  const { setSamples, setParams } = samplesSlice;
  const { setLoading } = commonSlice;

  if (action?.payload?.params) {
    yield put(setParams({ params: action.payload.params }));
  }

  try {
    yield put(setLoading({ isLoading: true }));
    const bodyParams: IGetManySamplesParams = yield select(selectParams);

    const response: GetManySamplesResponse = yield call(
      sendGetManySamplesRequest,
      bodyParams
    );
    const { items, count } = response.data.data;

    yield put(setSamples({ data: { items, count } }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

export function* handleRetrieveManySamplesById(
  action?: PayloadAction<GetManySamplesByIdPayload>
): Generator<any, void, any> {
  const { setMultiSamples } = samplesSlice;
  const { setLoading } = commonSlice;

  try {
    yield put(setLoading({ isLoading: true }));
    const bodyParams: IGetManySamplesParams = {biobankId: action?.payload?.biobankId};

    const response: GetManySamplesResponse = yield call(
      sendGetManySamplesByIdRequest,
      bodyParams
    );
    const { items, count } = response.data.data;

    yield put(setMultiSamples({ data: { items, count } }));
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

export function* handleDeleteSample(
  action: PayloadAction<DeleteSamplePayload>
): Generator<any, void, any> {
  const { sampleId, formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;

  try {
    yield call(sendDeleteSampleRequest, sampleId as number, formData);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("sample_deleted_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
    yield call(handleRetrieveManySamples);
  }
}

export function* handleImportSamples(
  action: PayloadAction<ImportSamplesPayload>
): Generator<any, void, any> {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { setLoadingDialog } = dialogSlice;

  try {
    yield put(setLoadingDialog({ isLoading: true }))

    yield call(sendImportSamplesRequest, formData as any);
    yield put(
      updateAlert({
        status: AlertStatusMessage.INFO,
        message: `${t("your_file_has_been_successfully_uploaded")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingDialog({ isLoading: false }))
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
  }
}

export function* handleSetPriceSamples(
  action: PayloadAction<SetPricePayload>
): Generator<any, void, any> {
  const { formData } = action.payload;
  const { updateAlert } = alertsSagaActions;
  const { toggleDialog } = dialogSagaActions;
  const { setLoadingDialog } = dialogSlice;

  try {
    yield put(setLoadingDialog({ isLoading: true }))
    yield call(sendSetPriceRequest, formData as any);
    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("price_has_been_set")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoadingDialog({ isLoading: false }))
    yield put(toggleDialog({ status: ToggleActionType.CLOSE }));
  }
}

export function* handleExportSamples(
  action: PayloadAction<ExportSamplesPayload>
): Generator<any, void, any> {
  const { updateAlert } = alertsSagaActions;
  const { setLoading } = commonSlice;

  try {
    yield put(setLoading({ isLoading: true }));

    const exportParams: IGetManySamplesParams = {
      skip: 0,
      limit: 1000000,
      status: GetManySamplesStatus.AVAILABLE,
      search: action.payload.params?.search,
    };

    const response: any = yield call(
      sendGetExportSamplesRequest,
      exportParams
    );

    const csvContent = response.data;

    if (!csvContent || csvContent.trim().length === 0) {
      yield put(
        updateAlert({
          status: AlertStatusMessage.INFO,
          message: `${t("there_is_no_data")}`,
        })
      );
      return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `samples_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    yield put(
      updateAlert({
        status: AlertStatusMessage.SUCCESS,
        message: `${t("samples_exported_successfully")}`,
      })
    );
  } catch (err) {
    yield call(handleError, err);
  } finally {
    yield put(setLoading({ isLoading: false }));
  }
}

function* handleError(err: any): Generator<any, void, any> {
  const { updateAlert } = alertsSagaActions;
  const error = err as IApiError;

  if (error?.response?.data?.error?.message?.length) {
    for (const text of error.response.data.error.message) {
      yield put(
        updateAlert({ status: AlertStatusMessage.ERROR, message: text })
      );
    }
  }
}
